import { Loader2 } from 'lucide-react';
import {
  type ButtonHTMLAttributes,
  forwardRef,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
  useId,
} from 'react';

import { cn } from '@/lib/utils';

/*
 * Contrôles de formulaire.
 *
 * Écrits à la main plutôt qu'importés d'une bibliothèque : le cahier des
 * charges (§19) demande une identité visuelle cohérente et des composants qui
 * ne donnent pas l'impression d'un assemblage. Une quinzaine de contrôles
 * maîtrisés valent mieux qu'un catalogue générique dont on surcharge le style.
 *
 * Deux règles tenues partout : hauteur de 36 px pour les contrôles standards
 * (compromis entre densité et confort de clic sur huit heures), et anneau de
 * focus systématique — l'application se pilote au clavier.
 */

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'subtle';
type ControlSize = 'sm' | 'md' | 'lg';

const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  primary:
    'bg-accent text-white shadow-sm hover:bg-accent-hover active:translate-y-px disabled:bg-accent/50',
  secondary:
    'bg-raised text-ink border border-line hover:bg-hover active:translate-y-px disabled:opacity-50',
  ghost: 'text-ink-muted hover:bg-hover hover:text-ink disabled:opacity-50',
  subtle:
    'bg-accent-soft text-accent-text border border-accent-border hover:bg-accent-soft/70 disabled:opacity-50',
  danger: 'bg-danger text-white shadow-sm hover:brightness-110 active:translate-y-px disabled:opacity-50',
};

const BUTTON_SIZES: Record<ControlSize, string> = {
  sm: 'h-8 px-3 text-xs gap-1.5',
  md: 'h-9 px-3.5 text-sm gap-2',
  lg: 'h-11 px-5 text-sm gap-2',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ControlSize;
  loading?: boolean;
  icon?: ReactNode;
  iconAfter?: ReactNode;
  fullWidth?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'secondary',
    size = 'md',
    loading = false,
    icon,
    iconAfter,
    fullWidth,
    className,
    children,
    disabled,
    type = 'button',
    ...props
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      // `aria-busy` plutôt qu'un simple libellé changé : un lecteur d'écran
      // annonce l'attente sans que le nom du bouton se mette à varier.
      aria-busy={loading || undefined}
      className={cn(
        'inline-flex items-center justify-center rounded-md font-medium whitespace-nowrap',
        'transition-colors duration-150 select-none',
        'disabled:pointer-events-none',
        BUTTON_VARIANTS[variant],
        BUTTON_SIZES[size],
        fullWidth && 'w-full',
        className,
      )}
      {...props}
    >
      {loading ? <Loader2 className="size-4 animate-spin" aria-hidden /> : icon}
      {children}
      {iconAfter}
    </button>
  );
});

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ControlSize;
  /** Obligatoire : un bouton sans texte doit porter son nom accessible. */
  label: string;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { variant = 'ghost', size = 'md', label, className, children, type = 'button', ...props },
  ref,
) {
  const dimensions = size === 'sm' ? 'size-8' : size === 'lg' ? 'size-11' : 'size-9';

  return (
    <button
      ref={ref}
      type={type}
      title={label}
      aria-label={label}
      className={cn(
        'inline-flex items-center justify-center rounded-md transition-colors duration-150',
        'disabled:pointer-events-none disabled:opacity-50',
        BUTTON_VARIANTS[variant],
        dimensions,
        className,
      )}
      {...props}
    >
      {children}
    </button>
  );
});

const CONTROL_BASE =
  'w-full rounded-md border border-line bg-raised text-ink placeholder:text-ink-faint ' +
  'transition-[border-color,box-shadow] duration-150 ' +
  'focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/25 ' +
  'disabled:cursor-not-allowed disabled:bg-sunken disabled:text-ink-faint';

const CONTROL_SIZES: Record<ControlSize, string> = {
  sm: 'h-8 px-2.5 text-xs',
  md: 'h-9 px-3 text-sm',
  lg: 'h-11 px-3.5 text-base',
};

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  size?: ControlSize;
  invalid?: boolean;
  leadingIcon?: ReactNode;
  trailingSlot?: ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { size = 'md', invalid, leadingIcon, trailingSlot, className, ...props },
  ref,
) {
  const field = (
    <input
      ref={ref}
      aria-invalid={invalid || undefined}
      className={cn(
        CONTROL_BASE,
        CONTROL_SIZES[size],
        leadingIcon && 'pl-9',
        trailingSlot && 'pr-10',
        invalid && 'border-danger focus:border-danger focus:ring-danger/25',
        className,
      )}
      {...props}
    />
  );

  if (!leadingIcon && !trailingSlot) return field;

  return (
    <div className="relative">
      {leadingIcon && (
        <span
          className="pointer-events-none absolute inset-y-0 left-0 flex w-9 items-center justify-center text-ink-faint"
          aria-hidden
        >
          {leadingIcon}
        </span>
      )}
      {field}
      {trailingSlot && (
        <span className="absolute inset-y-0 right-0 flex w-10 items-center justify-center">
          {trailingSlot}
        </span>
      )}
    </div>
  );
});

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  size?: ControlSize;
  invalid?: boolean;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { size = 'md', invalid, className, children, ...props },
  ref,
) {
  return (
    <select
      ref={ref}
      aria-invalid={invalid || undefined}
      className={cn(
        CONTROL_BASE,
        CONTROL_SIZES[size],
        'cursor-pointer appearance-none bg-no-repeat pr-8',
        invalid && 'border-danger focus:border-danger focus:ring-danger/25',
        className,
      )}
      style={{
        backgroundImage:
          "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E\")",
        backgroundPosition: 'right 0.6rem center',
        backgroundSize: '1rem',
      }}
      {...props}
    >
      {children}
    </select>
  );
});

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(
  function Textarea({ className, rows = 3, ...props }, ref) {
    return (
      <textarea
        ref={ref}
        rows={rows}
        className={cn(CONTROL_BASE, 'resize-y px-3 py-2 text-sm leading-relaxed', className)}
        {...props}
      />
    );
  },
);

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: ReactNode;
  hint?: string;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { label, hint, className, id, ...props },
  ref,
) {
  const generatedId = useId();
  const inputId = id ?? generatedId;

  return (
    <div className={cn('flex items-start gap-2.5', className)}>
      <input
        ref={ref}
        id={inputId}
        type="checkbox"
        className={cn(
          'mt-0.5 size-4 shrink-0 cursor-pointer rounded-sm border border-line-strong',
          'accent-[hsl(var(--accent))]',
          'focus:outline-none focus:ring-2 focus:ring-accent/25',
          'disabled:cursor-not-allowed disabled:opacity-50',
        )}
        {...props}
      />
      {(label || hint) && (
        <label htmlFor={inputId} className="cursor-pointer select-none text-sm leading-tight">
          {label && <span className="text-ink">{label}</span>}
          {hint && <span className="mt-0.5 block text-xs text-ink-faint">{hint}</span>}
        </label>
      )}
    </div>
  );
});

export interface FieldProps {
  label: string;
  htmlFor?: string;
  hint?: string;
  error?: string;
  required?: boolean;
  className?: string;
  children: ReactNode;
}

/** Enveloppe de champ : libellé, aide et erreur, disposés de façon constante. */
export function Field({
  label,
  htmlFor,
  hint,
  error,
  required,
  className,
  children,
}: FieldProps): React.JSX.Element {
  return (
    <div className={cn('space-y-1.5', className)}>
      <label
        htmlFor={htmlFor}
        className="flex items-center gap-1 text-xs font-medium text-ink-muted"
      >
        {label}
        {required && (
          <span className="text-danger" aria-hidden>
            *
          </span>
        )}
      </label>
      {children}
      {error ? (
        <p className="text-xs text-danger" role="alert">
          {error}
        </p>
      ) : hint ? (
        <p className="text-xs text-ink-faint">{hint}</p>
      ) : null}
    </div>
  );
}
